import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, Chip, Button, Stack, Divider,
  Avatar, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert,
} from '@mui/material';
import {
  LocationOn, Star, Wifi, Bolt, Water, LocalParking, Kitchen, Security,
  AcUnit, Checkroom, Group, Bed, School, ArrowBack, Bookmark, Share,
  ElectricalServices, Wash, Map, CheckCircle, Cancel, MeetingRoom,
  Tv, Bathtub, Desk, Chair, Air, Iron, WaterDrop, CleaningServices,
  Receipt, PhotoLibrary, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails, ImageList, ImageListItem } from '@mui/material';
import { propertiesAPI, bookingsAPI, reviewsAPI } from '../../api/client';
import { decimalOnly } from '../../utils/inputHelpers';
import useAuthStore from '../../store/useStore';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    check_in_date: '', check_out_date: '', notes: '',
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await propertiesAPI.getById(id);
        setProperty(res.data);
      } catch (err) {
        console.error('Failed to fetch property:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await reviewsAPI.listByProperty(id, { page: 1, per_page: 10 });
        setReviews(res.data.reviews || []);
        setReviewTotal(res.data.total || 0);
        setAvgRating(res.data.average_rating || 0);
      } catch (_) {}
    };
    fetchReviews();
  }, [id]);

  const handleSubmitReview = async () => {
    setReviewSubmitting(true);
    setReviewError('');
    try {
      await reviewsAPI.create({ property_id: id, ...reviewForm, rating: parseFloat(reviewForm.rating) });
      setReviewSuccess('Review submitted!');
      setReviewForm({ rating: 5, title: '', comment: '' });
      const res = await reviewsAPI.listByProperty(id, { page: 1, per_page: 10 });
      setReviews(res.data.reviews || []);
      setReviewTotal(res.data.total || 0);
      setAvgRating(res.data.average_rating || 0);
    } catch (err) {
      setReviewError(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setBookingLoading(true);
    setBookingError('');
    try {
      await bookingsAPI.create({
        property_id: property.id,
        check_in_date: new Date(bookingForm.check_in_date).toISOString(),
        check_out_date: new Date(bookingForm.check_out_date).toISOString(),
        notes: bookingForm.notes,
      });
      setBookingOpen(false);
      navigate('/dashboard/bookings');
    } catch (err) {
      setBookingError(err.response?.data?.detail || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}><Skeleton height={200} /><Skeleton /><Skeleton width="60%" /></Grid>
          <Grid item xs={12} md={4}><Skeleton height={300} /></Grid>
        </Grid>
      </Container>
    );
  }

  if (!property) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5">Property not found</Typography>
        <Button onClick={() => navigate('/properties')} sx={{ mt: 2 }}>Back to listings</Button>
      </Container>
    );
  }

  const amenities = [
    { key: 'has_electricity', label: 'Electricity', icon: <Bolt /> },
    { key: 'has_water', label: 'Water Supply', icon: <Water /> },
    { key: 'has_wifi', label: 'WiFi', icon: <Wifi /> },
    { key: 'has_security', label: 'Security', icon: <Security /> },
    { key: 'has_parking', label: 'Parking', icon: <LocalParking /> },
    { key: 'has_laundry', label: 'Laundry', icon: <Wash /> },
    { key: 'has_kitchen', label: 'Kitchen', icon: <Kitchen /> },
    { key: 'has_furnished', label: 'Furnished', icon: <Checkroom /> },
    { key: 'has_ac', label: 'Air Conditioning', icon: <AcUnit /> },
    { key: 'has_generator', label: 'Generator', icon: <ElectricalServices /> },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>

      {/* Image Gallery */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ height: { xs: 250, md: 400 }, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {property.images?.length > 0 ? (
            <Box component="img" src={property.images[selectedImage]?.image_url} alt={property.title}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Bed sx={{ fontSize: 100, color: 'grey.400' }} />
          )}
        </Box>
        {property.images?.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ p: 1.5, overflowX: 'auto' }}>
            {property.images.map((img, i) => (
              <Box key={img.id} onClick={() => setSelectedImage(i)}
                sx={{
                  width: 80, height: 60, borderRadius: 1, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                  border: selectedImage === i ? '2px solid' : '2px solid transparent', borderColor: 'primary.main',
                }}>
                <Box component="img" src={img.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={property.location_type === 'on_campus' ? 'On Campus' : 'Off Campus'}
              color={property.location_type === 'on_campus' ? 'primary' : 'secondary'} />
            <Chip label={property.property_type.replace('_', ' ')} variant="outlined" sx={{ textTransform: 'capitalize' }} />
            <Chip label={`${property.room_type} room`} variant="outlined" sx={{ textTransform: 'capitalize' }} />
            {property.allows_pairing && <Chip label="Pairing Available" icon={<Group />} color="success" />}
          </Box>

          <Typography variant="h4" fontWeight={700}>{property.title}</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 1 }}>
            <LocationOn color="action" />
            <Typography color="text.secondary">{property.address}, {property.city}, {property.region}</Typography>
          </Stack>
          {property.nearby_university && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <School color="primary" />
              <Typography color="primary.main" fontWeight={500}>{property.nearby_university}</Typography>
              {property.distance_to_campus && <Typography variant="body2" color="text.secondary">({property.distance_to_campus})</Typography>}
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Star sx={{ color: '#ffc107' }} />
            <Typography fontWeight={600}>{property.rating?.toFixed(1)}</Typography>
            <Typography variant="body2" color="text.secondary">({property.total_reviews} reviews)</Typography>
            <Typography variant="body2" color="text.secondary">&bull; {property.views_count} views</Typography>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>Description</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line' }}>{property.description}</Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom>Amenities</Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {amenities.map((a) => (
              <Grid item xs={6} sm={4} key={a.key}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ color: property[a.key] ? 'text.primary' : 'text.disabled' }}>
                  {a.icon}
                  <Typography variant="body2" sx={{ textDecoration: property[a.key] ? 'none' : 'line-through' }}>{a.label}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" fontWeight={600} gutterBottom>Room Details</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5" fontWeight={700} color="primary">{property.total_rooms}</Typography><Typography variant="body2" color="text.secondary">Total Rooms</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5" fontWeight={700} color="success.main">{property.available_rooms}</Typography><Typography variant="body2" color="text.secondary">Available</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5" fontWeight={700}>{property.max_occupants_per_room}</Typography><Typography variant="body2" color="text.secondary">Max per Room</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{property.room_type}</Typography><Typography variant="body2" color="text.secondary">Room Type</Typography></Paper></Grid>
          </Grid>

          {/* Individual Rooms Section */}
          {property.rooms?.length > 0 && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <MeetingRoom sx={{ verticalAlign: 'middle', mr: 1 }} />
                Individual Rooms ({property.rooms.length})
              </Typography>
              {property.rooms.map((room) => {
                const roomFeatures = [
                  { key: 'has_bed', label: 'Bed' },
                  { key: 'has_wardrobe', label: 'Wardrobe' },
                  { key: 'has_desk', label: 'Desk' },
                  { key: 'has_chair', label: 'Chair' },
                  { key: 'has_fan', label: 'Fan' },
                  { key: 'has_ac', label: 'Air Conditioning' },
                  { key: 'has_private_bathroom', label: 'Private Bathroom' },
                  { key: 'has_private_kitchen', label: 'Private Kitchen' },
                  { key: 'has_balcony', label: 'Balcony' },
                  { key: 'has_wifi', label: 'WiFi' },
                  { key: 'has_tv', label: 'TV' },
                  { key: 'has_fridge', label: 'Fridge' },
                  { key: 'has_microwave', label: 'Microwave' },
                  { key: 'has_washing_machine', label: 'Washing Machine' },
                  { key: 'has_iron', label: 'Iron' },
                  { key: 'has_water_heater', label: 'Water Heater' },
                  { key: 'has_curtains', label: 'Curtains' },
                  { key: 'has_mirror', label: 'Mirror' },
                  { key: 'has_shoe_rack', label: 'Shoe Rack' },
                ];
                const billsInfo = [
                  { key: 'bills_included', label: 'All Bills Included' },
                  { key: 'electricity_included', label: 'Electricity Included' },
                  { key: 'water_included', label: 'Water Included' },
                  { key: 'wifi_included', label: 'WiFi Included' },
                  { key: 'cleaning_included', label: 'Cleaning Included' },
                ];
                const activeFeatures = roomFeatures.filter(f => room[f.key]);
                const activeBills = billsInfo.filter(b => room[b.key]);
                const statusColor = room.status === 'available' ? 'success' : room.status === 'occupied' ? 'error' : 'warning';

                return (
                  <Accordion key={room.id} sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }} variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                        <Typography fontWeight={600}>{room.room_label || `Room ${room.room_number}`}</Typography>
                        <Chip label={room.status} size="small" color={statusColor} sx={{ textTransform: 'capitalize' }} />
                        <Chip label={`${room.room_type} room`} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        {room.price_per_month && (
                          <Typography variant="body2" fontWeight={600} color="primary" sx={{ ml: 'auto' }}>
                            {property.currency} {room.price_per_month.toLocaleString()}/mo
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {room.description && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">{room.description}</Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Room Info</Typography>
                          <Typography variant="body2">Max Occupants: {room.max_occupants}</Typography>
                          <Typography variant="body2">Current: {room.current_occupants}</Typography>
                          {room.floor && <Typography variant="body2">Floor: {room.floor}</Typography>}
                          {room.size_sqm && <Typography variant="body2">Size: {room.size_sqm} sqm</Typography>}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Pricing</Typography>
                          {room.price_per_month && <Typography variant="body2">{property.currency} {room.price_per_month.toLocaleString()} / month</Typography>}
                          {room.price_per_semester && <Typography variant="body2">{property.currency} {room.price_per_semester.toLocaleString()} / semester</Typography>}
                          {room.price_per_year && <Typography variant="body2">{property.currency} {room.price_per_year.toLocaleString()} / year</Typography>}
                        </Grid>
                        {activeFeatures.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Features & Accessories</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {activeFeatures.map(f => (
                                <Chip key={f.key} icon={<CheckCircle sx={{ fontSize: 14 }} />} label={f.label} size="small" color="success" variant="outlined" />
                              ))}
                            </Box>
                          </Grid>
                        )}
                        {activeBills.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Bills & Utilities Included</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {activeBills.map(b => (
                                <Chip key={b.key} icon={<Receipt sx={{ fontSize: 14 }} />} label={b.label} size="small" color="primary" variant="outlined" />
                              ))}
                            </Box>
                          </Grid>
                        )}
                        {room.images?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              <PhotoLibrary sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              Room Photos ({room.images.length})
                            </Typography>
                            <ImageList cols={4} rowHeight={120} gap={8}>
                              {room.images.map((img) => (
                                <ImageListItem key={img.id}>
                                  <Box component="img" src={img.image_url} alt={img.caption || 'Room'}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }} />
                                </ImageListItem>
                              ))}
                            </ImageList>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              <Box sx={{ mb: 3 }} />
            </>
          )}

          {/* Satellite Map View */}
          {property.latitude && property.longitude && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <Map sx={{ verticalAlign: 'middle', mr: 1 }} />
                Location Map
              </Typography>
              <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <Box
                  component="iframe"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${property.latitude},${property.longitude}&maptype=satellite&zoom=17`}
                  sx={{ width: '100%', height: 350, border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    GPS: {property.latitude.toFixed(6)}, {property.longitude.toFixed(6)}
                  </Typography>
                  <Button
                    size="small" variant="outlined" startIcon={<LocationOn />}
                    href={`https://www.google.com/maps?q=${property.latitude},${property.longitude}`}
                    target="_blank"
                  >
                    Open in Google Maps
                  </Button>
                </Box>
              </Paper>
            </>
          )}

          {property.rules && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>House Rules</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line' }}>{property.rules}</Typography>
            </>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 80 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Pricing</Typography>
            {property.price_per_month && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Monthly</Typography>
                <Typography fontWeight={700} color="primary">{property.currency} {property.price_per_month?.toLocaleString()}</Typography>
              </Box>
            )}
            {property.price_per_semester && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Per Semester</Typography>
                <Typography fontWeight={700} color="primary">{property.currency} {property.price_per_semester?.toLocaleString()}</Typography>
              </Box>
            )}
            {property.price_per_year && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Per Year</Typography>
                <Typography fontWeight={700} color="primary">{property.currency} {property.price_per_year?.toLocaleString()}</Typography>
              </Box>
            )}
            {property.deposit_amount && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography color="text.secondary">Deposit</Typography>
                <Typography fontWeight={600}>{property.currency} {property.deposit_amount?.toLocaleString()}</Typography>
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />

            {property.available_rooms > 0 ? (
              <Button fullWidth variant="contained" size="large" onClick={() => isAuthenticated ? setBookingOpen(true) : navigate('/login')} sx={{ mb: 1.5 }}>
                Book Now
              </Button>
            ) : (
              <Button fullWidth variant="contained" size="large" disabled sx={{ mb: 1.5 }}>Fully Booked</Button>
            )}
            {property.allows_pairing && (
              <Button fullWidth variant="outlined" size="large" startIcon={<Group />}
                onClick={() => isAuthenticated ? navigate('/dashboard/room-pairing') : navigate('/login')}>
                Find a Roommate
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Reviews Section */}
      <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Reviews ({reviewTotal})</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Star sx={{ color: '#ffc107' }} />
            <Typography fontWeight={700}>{avgRating.toFixed(1)}</Typography>
          </Stack>
        </Box>

        {reviews.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>No reviews yet. Be the first to review!</Typography>
        ) : (
          <Stack spacing={2} sx={{ mb: 3 }}>
            {reviews.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={r.user_avatar_url ? `${API_BASE}${r.user_avatar_url}` : undefined}
                      sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}
                    >
                      {(r.user_first_name?.[0] || r.user_id?.toString().slice(0, 1) || '?').toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {r.user_first_name ? `${r.user_first_name} ${r.user_last_name || ''}`.trim() : (r.title || 'Anonymous')}
                      </Typography>
                      {r.title && r.user_first_name && <Typography variant="caption" color="text.secondary">{r.title}</Typography>}
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} sx={{ fontSize: 16, color: s <= r.rating ? '#ffc107' : '#e0e0e0' }} />
                        ))}
                        {r.is_verified && <Chip label="Verified Stay" size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />}
                      </Stack>
                    </Box>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{new Date(r.created_at).toLocaleDateString()}</Typography>
                </Stack>
                {r.comment && <Typography variant="body2" sx={{ mt: 1 }}>{r.comment}</Typography>}
              </Paper>
            ))}
          </Stack>
        )}

        {isAuthenticated && property.owner_id !== user?.id && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Leave a Review</Typography>
            {reviewError && <Alert severity="error" sx={{ mb: 1 }}>{reviewError}</Alert>}
            {reviewSuccess && <Alert severity="success" sx={{ mb: 1 }}>{reviewSuccess}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Rating (1-5)" type="number" inputProps={{ min: 1, max: 5, step: 0.5 }}
                  value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: e.target.value })} onKeyDown={decimalOnly} />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField fullWidth label="Review Title (optional)" value={reviewForm.title}
                  onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Your review" multiline rows={3} value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSubmitReview} disabled={reviewSubmitting}>
                  {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onClose={() => setBookingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book This Property</DialogTitle>
        <DialogContent>
          {bookingError && <Alert severity="error" sx={{ mb: 2 }}>{bookingError}</Alert>}
          <TextField fullWidth label="Check-in Date" type="date" InputLabelProps={{ shrink: true }}
            value={bookingForm.check_in_date} onChange={(e) => setBookingForm({ ...bookingForm, check_in_date: e.target.value })} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Check-out Date" type="date" InputLabelProps={{ shrink: true }}
            value={bookingForm.check_out_date} onChange={(e) => setBookingForm({ ...bookingForm, check_out_date: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Additional Notes" multiline rows={3}
            value={bookingForm.notes} onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBookingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBooking} disabled={bookingLoading || !bookingForm.check_in_date || !bookingForm.check_out_date}>
            {bookingLoading ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PropertyDetailPage;
